import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import * as clientActions from '../../actions/client';
import * as agreementsActions from '../../actions/agreements';
import { useHistory, withRouter } from 'react-router';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { ParameterTypes } from '../../types/parameter.type';
import { checkIfUserIsAuthorized } from '../Admin/globalFunctions';
import './Agreements.scss';
import '../Measures/createMeasureForm.scss';
import DataTransferComponent from '../../components/DataTransferComponent/DataTransferComponent';
import MikaFileManager, { UploadSlot } from '../../components/MikaFileManager/MikaFileManager';
import MikaConfirmModal from '../../components/MikaConfirmModal/MikaConfirmModal';
import MentionInput from '../../components/MentionInput/MentionInput';
import MentionText from '../../components/MentionInput/MentionText';

const isMobile = window.innerWidth < 576;

const STATUS_VISUAL: Record<number, { color: string; bg: string }> = {
    1: { color: '#f59e0b', bg: '#fef3c7' },
    2: { color: '#3b82f6', bg: '#dbeafe' },
    3: { color: '#0891b2', bg: '#cffafe' },
    4: { color: '#22c55e', bg: '#dcfce7' },
    5: { color: '#ef4444', bg: '#fef2f2' },
};

const STATUS_I18N_KEY: Record<number, string> = {
    1: 'agreement.status.onHold',
    2: 'agreement.status.inProduction',
    3: 'agreement.status.inWarehouse',
    4: 'agreement.status.completed',
    5: 'agreement.status.canceled',
};

interface Props {
    intl: any;
    loading: boolean;
    client: any;
    clientActions: any;
    agreementsActions: any;
    actions: any;
    agreements: any;
    permissions: any;
    history: any;
    buttonLoader: boolean;
    companyProfile?: any;
}

const CreateAgreementForm = (props: Props) => {
    const fm = (id: string, defaultMessage: string, values?: Record<string, any>) =>
        props.intl.formatMessage({ id, defaultMessage }, values);

    const PAYMENT_METHODS = useMemo(() => [
        { value: 'cash', label: fm('agreement.payment.method.cash', 'Cash') },
        { value: 'bank', label: fm('agreement.payment.method.bank', 'Bank transfer') },
        { value: 'card', label: fm('agreement.payment.method.card', 'Card') },
        { value: 'other', label: fm('agreement.payment.method.other', 'Other') },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [props.intl]);

    const { agreementId, projectId } = useParams<ParameterTypes>();
    const history = useHistory();

    // Form state
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [agreement, setAgreement] = useState<any>(null);

    // File state
    const [finalAgreementFile, setFinalAgreementFile] = useState<any>({});
    const [annexes, setAnnexes] = useState<any[]>([]);
    const [signature, setSignature] = useState<any>({});
    const [cancelAgreementFile, setCancelAgreementFile] = useState<any>({});
    const [agreementAssets, setAgreementAssets] = useState<any[]>([]);
    const [cancellationAssets, setCancellationAssets] = useState<any[]>([]);
    const [filesLoading, setFilesLoading] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
    const [pendingBulkDeleteIds, setPendingBulkDeleteIds] = useState<number[]>([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Comments
    const [comments, setComments] = useState<any[]>([]);
    const [newComment, setNewComment] = useState('');

    // Payments
    const [payments, setPayments] = useState<any[]>([]);
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentDate, setPaymentDate] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    });
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [paymentNote, setPaymentNote] = useState('');

    // Status history
    const [statusHistory, setStatusHistory] = useState<any[]>([]);

    // Cancel payment modal
    const [cancelPaymentId, setCancelPaymentId] = useState<number | null>(null);
    const [cancelReason, setCancelReason] = useState('');

    const commentsEndRef = useRef<HTMLDivElement>(null);
    const [headerDropdownOpen, setHeaderDropdownOpen] = useState(false);

    // Permission check
    useEffect(() => {
        if (Object.keys(props.permissions).length !== 0 &&
            !checkIfUserIsAuthorized(props.permissions, props.permissions?.fullPrivileges, props.permissions?.agreements)) {
            toast.error(fm('agreement.toast.notAuthorized', 'You do not have permission to modify data in this module.'));
            props.history.push('/dashboard/agreements');
        }
    }, [props.permissions]);

    // Load agreement data
    useEffect(() => {
        if (agreementId) {
            setLoading(true);
            props.agreementsActions.fetchClientAgreement(Number(agreementId)).then((res: any) => {
                if (res?.data) {
                    setAgreement(res.data);
                    props.clientActions.fetchClient(res.data.clientId, 3);
                    parseFiles(res.data.agreementFiles || []);
                    // Load project details for DataTransferComponent
                    if (res.data.projectId) {
                        props.actions.getAllProjectInfo('', res.data.projectId);
                    }
                }
                setLoading(false);
            });
            loadComments();
            loadPayments();
            loadStatusHistory();
        } else {
            setLoading(false);
        }
    }, [agreementId]);

    const loadComments = () => {
        if (!agreementId) return;
        props.agreementsActions.getAgreementComments(Number(agreementId)).then((res: any) => {
            if (res?.data) setComments(res.data);
        });
    };

    const loadPayments = () => {
        if (!agreementId) return;
        props.agreementsActions.getAgreementPayments(Number(agreementId)).then((res: any) => {
            if (res?.data) setPayments(res.data);
        });
    };

    const loadStatusHistory = () => {
        if (!agreementId) return;
        props.agreementsActions.getAgreementStatusHistory(Number(agreementId)).then((res: any) => {
            if (res?.data) setStatusHistory(res.data);
        });
    };

    const parseFiles = (files: any[]) => {
        const assets: any[] = [];
        const cancel: any[] = [];
        files.forEach((file: any) => {
            if (file?.signatureWithUrl) assets.push({ id: file.id, name: file.signature || fm('agreement.form.files.signature', 'Signature'), url: file.signatureWithUrl, type: 'signature' });
            if (file?.finalAgreementWithUrl) assets.push({ id: file.id, name: file.finalAgreementName || fm('agreement.form.files.agreement', 'Agreement'), url: file.finalAgreementWithUrl, type: 'final' });
            if (file?.annexesWithUrl) assets.push({ id: file.id, name: file.annexes || fm('agreement.form.files.annex', 'Annex'), url: file.annexesWithUrl, type: 'annex' });
            if (file?.cancelationWithUrl) cancel.push({ id: file.id, name: file.cancelation || fm('agreement.form.files.cancellation', 'Cancellation'), url: file.cancelationWithUrl, type: 'cancel' });
        });
        setAgreementAssets(assets);
        setCancellationAssets(cancel);
    };

    // Calculated values
    const total = agreement?.total ? Number(String(agreement.total).replace(',', '')) : 0;
    const totalPaid = payments.filter(p => !p.canceled).reduce((sum, p) => sum + Number(p.amount), 0);
    const remainingDebt = total - totalPaid;
    const statusId = agreement?.agreementStatus?.value;
    const statusColors = STATUS_VISUAL[statusId] || { color: '#94a3b8', bg: '#f1f5f9' };
    const statusKey = STATUS_I18N_KEY[statusId];

    // ── Save ──────────────────────────────────────────────

    const handleSave = () => {
        if (saving) return;
        setSaving(true);

        const payload = new FormData();
        if (agreement?.id) payload.append('agreementId', agreement.id);
        if (props.client?.client?.id) payload.append('clientId', props.client.client.id);
        if (agreement?.offerId) payload.append('offerId', String(agreement.offerId));
        payload.append('comment', agreement?.comment || '');
        payload.append('isCanceled', '0');

        if (finalAgreementFile?.file) payload.append('finalAgreement[]', finalAgreementFile.file);
        if (signature?.file) payload.append('signature[]', signature.file);
        if (cancelAgreementFile?.file) payload.append('cancelation[]', cancelAgreementFile.file);
        if (annexes?.length) annexes.forEach((a) => payload.append('annexes[]', a.file));

        const action = agreement?.id ? props.agreementsActions.editAgreement : props.agreementsActions.createAgreement;
        action(payload).then(() => {
            toast.info(agreement?.id ? fm('agreement.toast.saved', 'Agreement saved successfully!') : fm('agreement.toast.created', 'Agreement created successfully!'));
            history.goBack();
        }).catch(() => {
            setSaving(false);
        });
    };

    // ── Comments ──────────────────────────────────────────

    const handleAddComment = () => {
        if (!newComment.trim() || !agreementId) return;
        props.agreementsActions.addAgreementComment(Number(agreementId), newComment.trim()).then((res: any) => {
            const comment = res?.data?.data || res?.data;
            if (comment) {
                setComments(prev => [...prev, comment]);
                setNewComment('');
                setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
            }
        });
    };

    const handleDeleteComment = (commentId: number) => {
        props.agreementsActions.deleteAgreementComment(commentId).then(() => {
            setComments(prev => prev.filter(c => c.id !== commentId));
        });
    };

    // ── Payments ──────────────────────────────────────────

    const handleAddPayment = () => {
        if (!paymentAmount || Number(paymentAmount) <= 0 || !agreementId) return;
        props.agreementsActions.addAgreementPayment(Number(agreementId), {
            amount: paymentAmount,
            paymentDate,
            paymentMethod,
            note: paymentNote || null,
        }).then((res: any) => {
            const payment = res?.data?.data || res?.data;
            if (payment) {
                setPayments(prev => [payment, ...prev]);
                setPaymentAmount('');
                setPaymentNote('');
                setShowPaymentForm(false);
            }
        });
    };

    const handleCancelPayment = () => {
        if (!cancelPaymentId || !cancelReason.trim()) return;
        props.agreementsActions.cancelAgreementPayment(cancelPaymentId, cancelReason.trim()).then(() => {
            setPayments(prev => prev.map(p => p.id === cancelPaymentId
                ? { ...p, canceled: true, canceledAt: new Date().toLocaleDateString('en-GB').replace(/\//g, '-'), canceledBy: 'Ti', canceledReason: cancelReason.trim() }
                : p
            ));
            setCancelPaymentId(null);
            setCancelReason('');
        });
    };

    // ── File removal ─────────────────────────────────────

    const handleRemoveFile = (fileId: number) => {
        setPendingDeleteId(fileId);
        setPendingBulkDeleteIds([]);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        setShowDeleteConfirm(false);
        setFilesLoading(true);
        try {
            if (pendingDeleteId != null) {
                await props.actions.removeFile('agreement', pendingDeleteId);
                setAgreementAssets(prev => prev.filter(a => a.id !== pendingDeleteId));
                setCancellationAssets(prev => prev.filter(a => a.id !== pendingDeleteId));
                setPendingDeleteId(null);
            } else if (pendingBulkDeleteIds.length > 0) {
                for (const id of pendingBulkDeleteIds) {
                    await props.actions.removeFile('agreement', id);
                    setAgreementAssets(prev => prev.filter(a => a.id !== id));
                    setCancellationAssets(prev => prev.filter(a => a.id !== id));
                }
                setPendingBulkDeleteIds([]);
            }
        } finally {
            setFilesLoading(false);
        }
    };

    const cancelDelete = () => {
        setShowDeleteConfirm(false);
        setPendingDeleteId(null);
        setPendingBulkDeleteIds([]);
    };

    if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}><div className="cmf-spinner" /></div>;

    const isEditing = !!agreement?.id;
    const clientName = props.client?.client
        ? `${props.client.client.firstnameAndLastname || ''} ${props.client.client.lastName || ''}`.trim()
        : '';

    return (
        <div className="agr-page">
            {/* ── Header — clickable to show project info dropdown ── */}
            <div className="cmf-header-wrap">
                <div className="cmf-header" onClick={() => setHeaderDropdownOpen(!headerDropdownOpen)} style={{ cursor: 'pointer' }}>
                    <div className="cmf-header-left">
                        <div className="cmf-header-info">
                            <h1 className="cmf-title">{clientName || fm('agreement.form.files.agreement', 'Agreement')}{agreement?.id ? ` #${agreement.id}` : ''}</h1>
                            <span className="cmf-subtitle">
                                {[agreement?.project?.label, agreement?.approvedAt ? `${fm('agreement.form.approvedLabel', 'Approved')}: ${agreement.approvedAt}` : null].filter(Boolean).join(' · ')}
                            </span>
                        </div>
                        {agreement?.agreementStatus && (
                            <span className="cmf-status-badge" style={{ color: statusColors.color, background: statusColors.bg }}>
                                {statusKey
                                    ? <FormattedMessage id={statusKey} defaultMessage={agreement.agreementStatus.label || '\u2014'} />
                                    : (agreement.agreementStatus.label || '\u2014')}
                            </span>
                        )}
                    </div>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2"
                        style={{ transform: headerDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </div>
                {headerDropdownOpen && (
                    <div className="cmf-header-dropdown">
                        <DataTransferComponent clientId={props.client?.client?.id} measureNeeded offerNeeded />
                    </div>
                )}
            </div>

            <div className={`agr-body ${isMobile ? 'agr-body-mobile' : ''}`}>
                {/* ── Left Panel ─────────────────────────────── */}
                <div className="agr-left">

                    {/* Financial summary */}
                    <div className="agr-section">
                        <div className="agr-section-title"><FormattedMessage id="agreement.form.financialSummary" defaultMessage="Financial summary" /></div>
                        <div className="agr-section-body">
                        <div className="agr-fin-cards">
                            <div className="agr-fin-card">
                                <span className="agr-fin-label"><FormattedMessage id="agreement.list.column.total" defaultMessage="Total" /></span>
                                <span className="agr-fin-value" style={{ color: '#64748b' }}>{total.toFixed(2)}&euro;</span>
                            </div>
                            <div className="agr-fin-card">
                                <span className="agr-fin-label"><FormattedMessage id="agreement.form.paid" defaultMessage="Paid" /></span>
                                <span className="agr-fin-value" style={{ color: '#22c55e' }}>{totalPaid.toFixed(2)}&euro;</span>
                            </div>
                            <div className="agr-fin-card">
                                <span className="agr-fin-label"><FormattedMessage id="agreement.form.debt" defaultMessage="Debt" /></span>
                                <span className="agr-fin-value" style={{ color: remainingDebt > 0 ? '#ef4444' : '#22c55e' }}>{remainingDebt.toFixed(2)}&euro;</span>
                            </div>
                        </div>
                        </div>
                    </div>

                    {/* KuBIT invoice status (read-only) — surfaced from the
                        synced fiscal sale on this agreement's offer. Hidden unless
                        KuBIT is enabled for the company and the backend returned a
                        kubitSale block (null when KuBIT is off for the deployment). */}
                    {isEditing && agreement?.offerId && props.companyProfile?.kubitEnabled && agreement?.kubitSale && (
                        <div className="agr-section">
                            <div className="agr-section-title">
                                <FormattedMessage id="integrations.kubit.invoice.title" defaultMessage="KuBIT invoice" />
                            </div>
                            <div className="agr-section-body">
                                {agreement.kubitSale.invoiced ? (
                                    <>
                                        <div className="agr-fin-cards">
                                            <div className="agr-fin-card">
                                                <span className="agr-fin-label"><FormattedMessage id="integrations.kubit.invoice.number" defaultMessage="Invoice no." /></span>
                                                <span className="agr-fin-value" style={{ color: '#1e3a5f' }}>#{agreement.kubitSale.invoiceNo}</span>
                                            </div>
                                            <div className="agr-fin-card">
                                                <span className="agr-fin-label"><FormattedMessage id="integrations.kubit.invoice.total" defaultMessage="Invoiced" /></span>
                                                <span className="agr-fin-value" style={{ color: '#64748b' }}>{Number(agreement.kubitSale.total).toFixed(2)}&euro;</span>
                                            </div>
                                            <div className="agr-fin-card">
                                                <span className="agr-fin-label"><FormattedMessage id="integrations.kubit.invoice.paid" defaultMessage="Paid" /></span>
                                                <span className="agr-fin-value" style={{ color: '#22c55e' }}>{Number(agreement.kubitSale.paid).toFixed(2)}&euro;</span>
                                            </div>
                                            <div className="agr-fin-card">
                                                <span className="agr-fin-label"><FormattedMessage id="integrations.kubit.invoice.remaining" defaultMessage="Remaining" /></span>
                                                <span className="agr-fin-value" style={{ color: Number(agreement.kubitSale.remaining) > 0 ? '#ef4444' : '#22c55e' }}>{Number(agreement.kubitSale.remaining).toFixed(2)}&euro;</span>
                                            </div>
                                        </div>
                                        <div className="agr-kubit-meta">
                                            <span className={`agr-kubit-badge ${agreement.kubitSale.fiscalized ? 'is-on' : 'is-off'}`}>
                                                <FormattedMessage
                                                    id={agreement.kubitSale.fiscalized ? 'integrations.kubit.invoice.fiscalized' : 'integrations.kubit.invoice.notFiscalized'}
                                                    defaultMessage={agreement.kubitSale.fiscalized ? 'Fiscalized' : 'Not fiscalized'}
                                                />
                                            </span>
                                            {agreement.kubitSale.invoiceDate && (
                                                <span className="agr-kubit-date">{agreement.kubitSale.invoiceDate}</span>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <div className="agr-kubit-empty">
                                        <FormattedMessage id="integrations.kubit.invoice.notInvoiced" defaultMessage="Not invoiced in KuBIT yet" />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* KuBIT manufacturing status (read-only) — surfaced from the
                        synced ProductManufacturing document for this offer. Same
                        gating as the invoice panel; only appears when the pushed
                        order actually had manufactured items. */}
                    {isEditing && agreement?.offerId && props.companyProfile?.kubitEnabled && agreement?.kubitManufacturing && (
                        <div className="agr-section">
                            <div className="agr-section-title">
                                <FormattedMessage id="integrations.kubit.manufacturing.title" defaultMessage="KuBIT manufacturing" />
                            </div>
                            <div className="agr-section-body">
                                {agreement.kubitManufacturing.manufactured ? (
                                    <>
                                        <div className="agr-fin-cards">
                                            <div className="agr-fin-card">
                                                <span className="agr-fin-label"><FormattedMessage id="integrations.kubit.manufacturing.document" defaultMessage="Document no." /></span>
                                                <span className="agr-fin-value" style={{ color: '#1e3a5f' }}>#{agreement.kubitManufacturing.documentNo}</span>
                                            </div>
                                            <div className="agr-fin-card">
                                                <span className="agr-fin-label"><FormattedMessage id="integrations.kubit.manufacturing.status" defaultMessage="Status" /></span>
                                                <span className="agr-fin-value" style={{ color: agreement.kubitManufacturing.validated ? '#22c55e' : '#f59e0b' }}>
                                                    <FormattedMessage
                                                        id={agreement.kubitManufacturing.validated ? 'integrations.kubit.manufacturing.validated' : 'integrations.kubit.manufacturing.pending'}
                                                        defaultMessage={agreement.kubitManufacturing.validated ? 'Validated' : 'In production'}
                                                    />
                                                </span>
                                            </div>
                                            {agreement.kubitManufacturing.outputsTotal != null && (
                                                <div className="agr-fin-card">
                                                    <span className="agr-fin-label"><FormattedMessage id="integrations.kubit.manufacturing.consumedLabel" defaultMessage="Materials consumed" /></span>
                                                    <span className="agr-fin-value" style={{ color: agreement.kubitManufacturing.outputsPending === 0 ? '#22c55e' : '#64748b' }}>
                                                        <FormattedMessage
                                                            id="integrations.kubit.manufacturing.consumedCount"
                                                            defaultMessage="{validated} of {total}"
                                                            values={{ validated: agreement.kubitManufacturing.outputsValidated, total: agreement.kubitManufacturing.outputsTotal }}
                                                        />
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="agr-kubit-meta">
                                            <span className={`agr-kubit-badge ${agreement.kubitManufacturing.validated ? 'is-on' : 'is-off'}`}>
                                                <FormattedMessage
                                                    id={agreement.kubitManufacturing.validated ? 'integrations.kubit.manufacturing.stockConsumed' : 'integrations.kubit.manufacturing.stockPending'}
                                                    defaultMessage={agreement.kubitManufacturing.validated ? 'Stock consumed' : 'Awaiting consumption'}
                                                />
                                            </span>
                                            {agreement.kubitManufacturing.lastSyncedAt && (
                                                <span className="agr-kubit-date">{agreement.kubitManufacturing.lastSyncedAt}</span>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <div className="agr-kubit-empty">
                                        <FormattedMessage id="integrations.kubit.manufacturing.none" defaultMessage="No manufacturing document in KuBIT" />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Payments */}
                    {isEditing && (
                        <div className="agr-section">
                            <div className="agr-section-title">
                                <FormattedMessage id="agreement.form.paymentHistory" defaultMessage="Payment history" />
                                <button className="agr-add-btn" onClick={() => setShowPaymentForm(!showPaymentForm)} type="button">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                                    <FormattedMessage id="agreement.form.addPayment" defaultMessage="Add payment" />
                                </button>
                            </div>
                            <div className="agr-section-body">

                            {showPaymentForm && (
                                <div className="agr-payment-form">
                                    <div className="agr-payment-row">
                                        <div className="agr-payment-field">
                                            <label><FormattedMessage id="agreement.form.payment.amount" defaultMessage="Amount" /></label>
                                            <input type="number" min="0" step="0.01" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} placeholder="0.00" />
                                        </div>
                                        <div className="agr-payment-field">
                                            <label><FormattedMessage id="agreement.form.payment.date" defaultMessage="Date" /></label>
                                            <input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} />
                                        </div>
                                        <div className="agr-payment-field">
                                            <label><FormattedMessage id="agreement.form.payment.method" defaultMessage="Method" /></label>
                                            <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                                                {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="agr-payment-row">
                                        <div className="agr-payment-field agr-payment-field-full">
                                            <label><FormattedMessage id="agreement.form.payment.noteOptional" defaultMessage="Note (optional)" /></label>
                                            <input type="text" value={paymentNote} onChange={e => setPaymentNote(e.target.value)} placeholder={fm('agreement.form.payment.notePlaceholder', 'Note...')} />
                                        </div>
                                    </div>
                                    <div className="agr-payment-actions">
                                        <button className="agr-btn-secondary" onClick={() => setShowPaymentForm(false)} type="button"><FormattedMessage id="common.confirm.cancel" defaultMessage="Cancel" /></button>
                                        <button className="agr-btn-primary" onClick={handleAddPayment} type="button"><FormattedMessage id="agreement.form.payment.add" defaultMessage="Add" /></button>
                                    </div>
                                </div>
                            )}

                            {payments.length === 0 ? (
                                <div className="agr-empty"><FormattedMessage id="agreement.form.noPayments" defaultMessage="No payments registered" /></div>
                            ) : (
                                <div className="agr-payment-list">
                                    {payments.map(p => (
                                        <div key={p.id} className={`agr-payment-item ${p.canceled ? 'agr-payment-canceled' : ''}`}>
                                            <div className="agr-payment-row-top">
                                                <div className="agr-payment-info">
                                                    <span className="agr-payment-amount">{Number(p.amount).toFixed(2)}&euro;</span>
                                                    <span className="agr-payment-method">{PAYMENT_METHODS.find(m => m.value === p.paymentMethod)?.label || p.paymentMethod}</span>
                                                </div>
                                                <div className="agr-payment-meta">
                                                    <span>{p.createdAt}</span>
                                                    <span className="agr-payment-user">{p.user}</span>
                                                    {p.note && <span className="agr-payment-note">{p.note}</span>}
                                                </div>
                                                {!p.canceled && (
                                                    <button className="agr-payment-cancel-btn" onClick={() => setCancelPaymentId(p.id)} type="button" title={fm('agreement.form.cancelPaymentTooltip', 'Cancel')}>
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                                                    </button>
                                                )}
                                            </div>
                                            {p.canceled && (
                                                <div className="agr-payment-cancel-bar">
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                                                    <span><FormattedMessage id="agreement.form.canceledBy" defaultMessage="Canceled by {user} · {date}" values={{ user: <strong>{p.canceledBy}</strong>, date: p.canceledAt }} /></span>
                                                    {p.canceledReason && <span className="agr-payment-cancel-reason">&mdash; "{p.canceledReason}"</span>}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            </div>
                        </div>
                    )}

                    {/* Comments */}
                    {isEditing && (
                        <div className="agr-section">
                            <div className="agr-section-title">
                                <FormattedMessage id="agreement.form.commentsSection" defaultMessage="Comments" />
                                {comments.length > 0 && <span className="agr-comment-count">{comments.length}</span>}
                            </div>
                            <div className="agr-section-body">
                            <div className="agr-comments">
                                {comments.length === 0 && (
                                    <div className="agr-comments-empty">
                                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
                                        <span><FormattedMessage id="agreement.form.noCommentsYet" defaultMessage="No comments yet" /></span>
                                    </div>
                                )}
                                {comments.map(c => {
                                    const initials = (c.user || '??').split(' ').map((w: string) => w.charAt(0)).join('').substring(0, 2).toUpperCase();
                                    const colors = ['#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#ef4444', '#22c55e', '#ec4899', '#6366f1'];
                                    let hash = 0;
                                    for (let i = 0; i < (c.user || '').length; i++) hash = (c.user || '').charCodeAt(i) + ((hash << 5) - hash);
                                    const avatarColor = colors[Math.abs(hash) % colors.length];
                                    return (
                                        <div key={c.id} className="agr-comment">
                                            <div className="agr-comment-avatar" style={{ background: avatarColor }}>{initials}</div>
                                            <div className="agr-comment-content">
                                                <div className="agr-comment-meta">
                                                    <span className="agr-comment-user">{c.user}</span>
                                                    <span className="agr-comment-dot">&middot;</span>
                                                    <span className="agr-comment-date">{c.createdAt}</span>
                                                </div>
                                                <div className="agr-comment-body"><MentionText text={c.comment} /></div>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={commentsEndRef} />
                            </div>
                            <div className="agr-comment-input-wrap">
                                <MentionInput
                                    multiline
                                    value={newComment}
                                    onChange={(next) => setNewComment(next)}
                                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); }}}
                                    placeholder={fm('agreement.form.writeComment', 'Write a comment...')}
                                    rows={2}
                                />
                                <button className="agr-comment-send" onClick={handleAddComment} disabled={!newComment.trim()} type="button" title={fm('offer.form.sendComment', 'Send')}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                                </button>
                            </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Right Panel ────────────────────────────── */}
                <div className="agr-right">

                    {/* Documents */}
                    <div className="agr-section">
                        <div className="agr-section-title"><FormattedMessage id="agreement.form.documentsSection" defaultMessage="Documents" /></div>
                        <div className="agr-section-body">
                            <MikaFileManager
                                files={[
                                    ...agreementAssets.map(a => ({ ...a, group: 'agreement' })),
                                    ...cancellationAssets.map(a => ({ ...a, group: 'cancellation' })),
                                ]}
                                groups={[
                                    { key: 'agreement', label: fm('agreement.form.files.groupAgreement', 'Agreement files') },
                                    { key: 'cancellation', label: fm('agreement.form.files.groupCancellation', 'Cancellation files'), color: '#ef4444' },
                                ]}
                                uploadSlots={[
                                    { key: 'finalAgreement', label: fm('agreement.form.files.finalAgreement', 'Final agreement'), icon: 'file' },
                                    { key: 'annexes', label: fm('agreement.form.files.annexes', 'Annexes'), icon: 'files', multiple: true },
                                    { key: 'signature', label: fm('agreement.form.files.signatureUpload', 'Signature'), icon: 'image', accept: 'image/*' },
                                    { key: 'cancelation', label: fm('agreement.form.files.cancelation', 'Withdrawal/Cancellation'), icon: 'alert' },
                                ]}
                                onUpload={(key, files) => {
                                    if (key === 'finalAgreement') setFinalAgreementFile({ file: files[0], fileName: files[0].name });
                                    else if (key === 'annexes') setAnnexes(files.map(f => ({ file: f, fileName: f.name })));
                                    else if (key === 'signature') setSignature({ file: files[0], fileName: files[0].name });
                                    else if (key === 'cancelation') setCancelAgreementFile({ file: files[0], fileName: files[0].name });
                                }}
                                loading={filesLoading}
                                onDelete={handleRemoveFile}
                                onBulkDelete={(ids) => {
                                    setPendingBulkDeleteIds(ids);
                                    setPendingDeleteId(null);
                                    setShowDeleteConfirm(true);
                                }}
                            />
                        </div>
                    </div>

                    {/* Status history */}
                    {isEditing && statusHistory.length > 0 && (
                        <div className="agr-section">
                            <div className="agr-section-title"><FormattedMessage id="agreement.form.statusHistory" defaultMessage="Status history" /></div>
                            <div className="agr-section-body">
                            <div className="agr-status-timeline">
                                {statusHistory.map((s) => {
                                    const isSystem = s.triggerType === 'system';
                                    const fromKey = STATUS_I18N_KEY[s.fromStatusId];
                                    const toKey = STATUS_I18N_KEY[s.toStatusId];
                                    return (
                                        <div key={s.id} className="agr-status-item">
                                            <div className="agr-status-dot" style={{ background: STATUS_VISUAL[s.toStatusId]?.color || '#94a3b8' }} />
                                            <div className="agr-status-content">
                                                <div className="agr-status-transition">
                                                    {s.fromStatus && <><span>{fromKey ? <FormattedMessage id={fromKey} defaultMessage={s.fromStatus} /> : s.fromStatus}</span> <span className="agr-status-arrow">&rarr;</span></>}
                                                    <span style={{ color: STATUS_VISUAL[s.toStatusId]?.color || '#0f172a', fontWeight: 600 }}>{toKey ? <FormattedMessage id={toKey} defaultMessage={s.toStatus} /> : s.toStatus}</span>
                                                </div>
                                                <div className="agr-status-meta">
                                                    <span className={`agr-trigger-badge ${isSystem ? 'agr-trigger-system' : 'agr-trigger-manual'}`}>
                                                        {isSystem ? fm('agreement.form.system', 'System') : s.user || 'Manual'}
                                                    </span>
                                                    <span className="agr-status-date">{s.createdAt}</span>
                                                </div>
                                                {s.triggerDetail && <div className="agr-status-detail">{s.triggerDetail}</div>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Delete file confirm modal ────────────────────── */}
            <MikaConfirmModal
                isOpen={showDeleteConfirm}
                onClose={cancelDelete}
                onConfirm={confirmDelete}
                title={fm('agreement.deleteConfirm.title', 'Are you sure?')}
                message={
                    pendingDeleteId != null
                        ? fm('agreement.deleteConfirm.single', 'Do you want to delete "{name}"?', {
                            name: [...agreementAssets, ...cancellationAssets].find(a => a.id === pendingDeleteId)?.name || ''
                        })
                        : fm('agreement.deleteConfirm.bulk', 'Do you want to delete {count, plural, one {# file} other {# files}}?', {
                            count: pendingBulkDeleteIds.length
                        })
                }
                confirmLabel={fm('agreement.deleteConfirm.confirmDelete', 'Delete')}
                confirmColor="danger"
            />

            {/* ── Cancel payment modal ────────────────────────── */}
            {cancelPaymentId && (
                <div className="agr-modal-overlay" onClick={() => { setCancelPaymentId(null); setCancelReason(''); }}>
                    <div className="agr-modal" onClick={e => e.stopPropagation()}>
                        <div className="agr-modal-header"><FormattedMessage id="agreement.form.cancelPayment" defaultMessage="Cancel payment" /></div>
                        <div className="agr-modal-body">
                            <p className="agr-modal-text"><FormattedMessage id="agreement.form.cancelPaymentInstruction" defaultMessage="This payment will be marked as canceled. Provide the reason:" /></p>
                            <textarea className="agr-modal-textarea" value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder={fm('agreement.form.cancelPaymentReason', 'Reason for cancellation...')} rows={3} autoFocus />
                        </div>
                        <div className="agr-modal-actions">
                            <button className="agr-btn-secondary" onClick={() => { setCancelPaymentId(null); setCancelReason(''); }} type="button"><FormattedMessage id="agreement.form.goBack" defaultMessage="Back" /></button>
                            <button className="agr-btn-danger" onClick={handleCancelPayment} disabled={!cancelReason.trim()} type="button"><FormattedMessage id="agreement.form.cancelPayment" defaultMessage="Cancel payment" /></button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Sticky save bar ────────────────────────────── */}
            {((!props.permissions?.observer) || props.permissions?.fullPrivileges) && props.client?.client?.id && (
                <div className="agr-save-bar">
                    <button className="agr-btn-primary agr-btn-save" onClick={handleSave} disabled={saving} type="button">
                        {saving
                            ? <FormattedMessage id="agreement.form.saving" defaultMessage="Saving..." />
                            : isEditing
                                ? <FormattedMessage id="common.action.save" defaultMessage="Save" />
                                : <FormattedMessage id="common.action.create" defaultMessage="Create" />}
                    </button>
                </div>
            )}
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        ...state.app,
        client: state.client,
        agreements: state.agreements,
    };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        agreementsActions: bindActionCreators(agreementsActions as any, dispatch),
        clientActions: bindActionCreators(clientActions as any, dispatch),
        actions: bindActionCreators(actions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(CreateAgreementForm as any)));
